import * as React from "react";
import {
  redirect,
  RouterProvider,
  createBrowserRouter
} from "react-router-dom";
import { PrivateRoute } from "./PrivateRoute";
import ContactCenter from "@/pages/ContactCenter";
import Home from "@/pages/Home";
import EntraCallback from "@/pages/Auth/EntraCallback";
import LoginCallback from "@/pages/Auth/LoginCallback";
import LoginCallbackError from "@/pages/Auth/LoginCallbackError";
import LoginUnauthorized from "@/pages/Auth/LoginUnauthorized";
import Logout from "@/pages/Auth/Logout";
import LogoutCallback from "@/pages/Auth/LogoutCallback";
import AutoResponses from "@/pages/Messaging/AutoResponses/AutoResponses";
import CannedResponses from "@/pages/Messaging/CannedResponses/CannedResponses";
import TwitterCallback from "@/pages/Messaging/Channels/TwitterCallback";
import IntegrationLog from "@/pages/Messaging/Integrations/Log";
import NetSuite, {
  BackToIntegrationLanding
} from "@/pages/Messaging/Integrations/NetSuite/NetSuite";
import NetSuiteCreate from "@/pages/Messaging/Integrations/NetSuite/NetSuiteCreate";
import NetSuiteCallback from "@/pages/Messaging/Integrations/NetSuite/NetSuiteCallback";
import Surveys from "@/pages/Messaging/Surveys/Surveys";
import Teams from "@/pages/Messaging/Teams/Teams";
import UserProfile, {
  BackToUsersButton
} from "@/pages/Messaging/Users/UserProfile";
import WebMessenger from "@/pages/Messaging/WebMessenger";
import WorkSchedules from "@/pages/Messaging/WorkSchedules/WorkSchedules";
import Users from "@/pages/Messaging/Users/Users";
import AddUserWizard from "@/pages/Messaging/Users/AddUserWizard/AddUserWizard";
import Deployment from "@/pages/Messaging/Deployments/Deployments";
import LivePreviewButton from "@/pages/Messaging/Deployments/LivePreviewButton";
import BotManager from "@/pages/Messaging/Bots";
import RuleManager from "@/pages/Messaging/Rules";
import useAuth from "@/hooks/useAuth";
import { useCallback } from "react";
import Landing from "@/pages/Messaging/Integrations/IntegrationsLanding";
import FileStorage from "@/pages/Messaging/FileStorage";
import WorkflowsCreate from "@/pages/Messaging/Workflows/WorkflowsCreate";
import Workflows from "@/pages/Messaging/Workflows/Workflows";
import FacebookIntegration from "@/pages/Messaging/Channels/FacebookIntegration";
import SmsIntegration from "@/pages/Messaging/Channels/SmsIntegration";
import WhatsAppIntegration from "@/pages/Messaging/Channels/WhatsAppIntegration";
import ArticlesIndex from "@/kb/articles/Index";
import Skills from "@/pages/Messaging/Skills/Skills";
import ArticlesCreate from "@/kb/articles/Create";
import ArticlesEdit from "@/kb/articles/Edit";
import SettingsIndex from "@/kb/settings/Index";
import TopicsIndex from "@/kb/topics/Index";
import SiteMap from "@/kb/sitemap/Index";
import SetupIndex from "@/kb/setup/Index";
import MessagingSetup from "@/pages/Messaging/Setup/Index";
import DatabaseTool from "@/pages/Admin/DatabaseTool";
import LiveMonitor from "@/pages/Admin/LiveMonitor";
import AI from "@/pages/Messaging/Bots/AI/AI";
import CreateChatbotButton from "@/pages/Messaging/Bots/AI/CreateChatbotButton";
import NewWorkflowButton from "@/pages/Messaging/Workflows/NewWorkflowButton";
import AIChatbotSettingsGeneral from "@/pages/Messaging/Bots/AI/AIChatbotSettingsGeneral";
import StickyRouteHeader from "@/pages/Shared/StickyRouteHeader";
import AIChatbotSettingsTraining from "@/pages/Messaging/Bots/AI/AIChatbotSettingsTraining";
import WorkflowsEditor from "@/pages/Messaging/Workflows/WorkflowsEditor";
import VariableGuide from "@/pages/Messaging/Workflows/VariableGuide";
import Formstack from "@/pages/Messaging/Integrations/Formstack/Formstack";
import FormstackCreate from "@/pages/Messaging/Integrations/Formstack/FormstackCreate";
import AIChatbotSettingsHandoff from "@/pages/Messaging/Bots/AI/AIChatbotSettingsHandoff";
import SaveChatbotButton from "@/pages/Messaging/Bots/AI/SaveChatbotButton";
import IvrIntegration from "@/pages/Messaging/Channels/IvrIntegration";
import AddPhoneNumberButton from "@/pages/Messaging/Channels/AddPhoneNumberButton";
import SiteSettings from "@/pages/Messaging/SiteSettings";
import Tags from "@/pages/Messaging/Tags/Tags";
import Invitations from "@/pages/Messaging/Invitations";
import BaseInvitationEditor from "@/pages/Messaging/Invitations/BaseInvitationEditor";
import AIChatbotSettingsPlayground from "@/pages/Messaging/Bots/AI/AIChatbotSettingsPlayground";
import SearchBar from "@/components/SearchBar";
import CreateIndexButton from "@/pages/Messaging/Bots/AI/CreateIndexButton";
import AIChatbotTrainingIndex from "@/pages/Messaging/Bots/AI/AIChatbotTrainingIndex";
import AIChatbotSettingsTrainingTabFile from "@/pages/Messaging/Bots/AI/AIChatbotFileTrainingIndex";
import AIChatbotFileTraining from "@/pages/Messaging/Bots/AI/AIChatbotFileTraining";
import AIChatbotWebTraining from "@/pages/Messaging/Bots/AI/AIChatbotWebTraining";
import AINewTraining from "@/pages/Messaging/Bots/AINew/AINewTraining";
import AINewChat from "@/pages/Messaging/Bots/AINew/AINewChat";
import AINewSettings from "@/pages/Messaging/Bots/AINew/AINewSettings";
import AINewSettingsGeneral from "@/pages/Messaging/Bots/AINew/AINewSettingsGeneral";
import AINewSettingsHandoff from "@/pages/Messaging/Bots/AINew/AINewSettingsHandoff";
import AINewSettingsPlayground from "@/pages/Messaging/Bots/AINew/AINewSettingsPlayground";
import AINewCreateChatbotButton from "@/pages/Messaging/Bots/AINew/AINewCreateChatbotButton";
import AINewSaveChatbotButton from "@/pages/Messaging/Bots/AINew/AINewSaveChatbotButton";
import AINewIndexes from "@/pages/Messaging/Bots/AINew/AINewIndexes";
import { RouteHeader, SecondaryBlueButton } from "@velaro/velaro-component-lib";
import AccelaCivic from "@/pages/Messaging/Integrations/Accela/Accela";
import AccelaCivicCreate from "@/pages/Messaging/Integrations/Accela/AccelaCreate";
import ChatWindow from "@/pages/Messaging/ChatWindow";
import ChatWindowEditor from "@/pages/Messaging/ChatWindow/ChatWindowEditor";
import CreateChatWindowButton from "@/pages/Messaging/ChatWindow/CreateChatWindowButton";
import AccelaCallbackBridge from "@/pages/Messaging/Integrations/Accela/AccelaCallbackBridge";
import CustomFields from "@/pages/ContactCenter/CustomFields";
import Shopify from "@/pages/Messaging/Integrations/Shopify/Shopify";
import ShopifyCreate, {
  ShopifyBackToShopifyOverview
} from "@/pages/Messaging/Integrations/Shopify/ShopifyCreate";
import ShopifyOverview, {
  ShopifyBackToIntegrationLanding
} from "@/pages/Messaging/Integrations/Shopify/ShopifyOverview";

const handleSearch = (searchText: string) => {};

const handleAccountSearch = (searchText: string) => {
  window.dispatchEvent(
    new CustomEvent("accountSearch", { detail: searchText })
  );
};

const createAppRoutes = (mainLoader: (params: any) => any) => [
  { path: "auth/callback", element: <EntraCallback /> },
  { path: "login/callback", element: <LoginCallback /> },
  { path: "login/callback/error", element: <LoginCallbackError /> },
  { path: "login/unauthorized", element: <LoginUnauthorized /> },
  { path: "logout", element: <Logout /> },
  { path: "logout/callback", element: <LogoutCallback /> },
  {
    element: <PrivateRoute />,
    children: [
      { path: "/", element: <Home /> },
      {
        path: "/account",
        element: <SiteSettings />
      },
      {
        path: "/admin/database-tool",
        element: (
          <RouteHeader title="Database Administration">
            <DatabaseTool />
          </RouteHeader>
        )
      },
      {
        path: "/admin/live-monitor",
        element: <LiveMonitor />
      },
      {
        path: "/users/",
        element: <Users />
      },
      {
        path: "/users/:userId",
        element: (
          <RouteHeader
            title="User Profile"
            headerActions={<BackToUsersButton />}
          >
            <UserProfile />
          </RouteHeader>
        )
      },
      {
        path: "/user/add",
        element: (
          <RouteHeader title="Add User">
            <AddUserWizard />
          </RouteHeader>
        )
      },
      {
        path: "/contact_center",
        element: (
          <RouteHeader title="Contact Center">
            <ContactCenter />
          </RouteHeader>
        )
      },
      {
        path: "/teams",
        element: <Teams />
      },
      {
        path: "/deployment",
        element: (
          <StickyRouteHeader
            title="Deployment"
            headerActions={<LivePreviewButton />}
          >
            <Deployment />
          </StickyRouteHeader>
        )
      },
      {
        path: "/tags",
        element: (
          <RouteHeader title="Tags">
            <Tags />
          </RouteHeader>
        )
      },
      {
        path: "/custom-fields",
        element: (
          <RouteHeader title="Custom Fields">
            <CustomFields />
          </RouteHeader>
        )
      },
      {
        path: "/facebook",
        element: (
          <RouteHeader title="Facebook">
            <FacebookIntegration />
          </RouteHeader>
        )
      },
      {
        path: "/sms",
        element: (
          <RouteHeader title="SMS">
            <SmsIntegration />
          </RouteHeader>
        )
      },
      {
        path: "/whatsapp",
        element: (
          <RouteHeader title="WhatsApp">
            <WhatsAppIntegration />
          </RouteHeader>
        )
      },
      {
        path: "/ivr",
        element: (
          <RouteHeader title="IVR" headerActions={<AddPhoneNumberButton />}>
            <IvrIntegration />
          </RouteHeader>
        )
      },
      {
        path: "/surveys",
        element: (
          <RouteHeader title="Surveys">
            <Surveys />
          </RouteHeader>
        )
      },
      {
        path: "/integrations",
        element: (
          <StickyRouteHeader
            title="Integrations"
            subTitle="Configure integrations to fit your specific business needs, enhancing your ability to manage everything in one place."
            headerActions={
              <SearchBar onSearch={handleSearch} placeholderText="Search" />
            }
          >
            <Landing />
          </StickyRouteHeader>
        )
      },
      {
        path: "/twitter/callback",
        element: <TwitterCallback />
      },
      {
        path: "/netsuite/oauthcallback",
        element: <NetSuiteCallback />
      },
      {
        path: "/auto_responses",
        element: (
          <RouteHeader title="Auto Responses">
            <AutoResponses />
          </RouteHeader>
        )
      },
      {
        path: "/canned_responses",
        element: <CannedResponses />
      },
      {
        path: "/web_messenger",
        element: (
          <RouteHeader title="Web Messenger">
            <WebMessenger />
          </RouteHeader>
        )
      },
      {
        path: "/chat_window",
        element: (
          <RouteHeader
            title="Chat Window"
            headerActions={<CreateChatWindowButton />}
          >
            <ChatWindow />
          </RouteHeader>
        )
      },
      {
        path: "/chat_window/:chatWindowId",
        element: <ChatWindowEditor />
      },
      {
        path: "/work_schedules",
        element: (
          <RouteHeader title="Work Schedules">
            <WorkSchedules />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/netsuite",
        element: (
          <RouteHeader
            title="Netsuite CRM"
            headerActions={<BackToIntegrationLanding />}
          >
            <NetSuite />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/netsuite/netsuitecreate",
        element: (
          <RouteHeader title="">
            <NetSuiteCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/formstack",
        element: (
          <RouteHeader title="Formstack">
            <Formstack />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/formstack/formstackcreate",
        element: (
          <RouteHeader title="">
            <FormstackCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/accela-civic",
        element: (
          <RouteHeader title="Accela">
            <AccelaCivic />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/accela-civic/AccelaCreate",
        element: (
          <RouteHeader title="Accela">
            <AccelaCivicCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/accela-civic/AccelaCallbackBridge",
        element: (
          <RouteHeader title="Accela">
            <AccelaCallbackBridge />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/Shopify",
        element: (
          <RouteHeader title="Shopify">
            <Shopify />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/shopify/ShopifyCreate",
        element: (
          <RouteHeader
            title=""
            headerActions={<ShopifyBackToShopifyOverview />}
          >
            <ShopifyCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/shopify/ShopifyOverview",
        element: (
          <RouteHeader
            title=""
            headerActions={<ShopifyBackToIntegrationLanding />}
          >
            <ShopifyOverview />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/log",
        element: (
          <RouteHeader title="Integration Log">
            <IntegrationLog />
          </RouteHeader>
        )
      },
      {
        path: "/bots",
        element: (
          <RouteHeader title="Bots">
            <BotManager />
          </RouteHeader>
        )
      },
      {
        path: "/bots/training/index",
        element: (
          <StickyRouteHeader
            title="AI Training"
            headerActions={<CreateIndexButton></CreateIndexButton>}
          >
            <AIChatbotTrainingIndex />
          </StickyRouteHeader>
        )
      },
      {
        path: "/bots/training/web",
        element: (
          <StickyRouteHeader title="AI Training" headerActions={null}>
            <AIChatbotWebTraining />
          </StickyRouteHeader>
        )
      },
      {
        path: "/bots/training/file",
        element: (
          <StickyRouteHeader title="AI Training" headerActions={null}>
            <AIChatbotFileTraining />
          </StickyRouteHeader>
        )
      },
      {
        path: "/ai",
        element: (
          <StickyRouteHeader
            title="AI Chatbot"
            headerActions={<CreateChatbotButton></CreateChatbotButton>}
          >
            <AI />
          </StickyRouteHeader>
        )
      },
      {
        path: "/ai/settings/general/:id",
        element: (
          <StickyRouteHeader
            title="AI Chatbot"
            headerActions={<SaveChatbotButton></SaveChatbotButton>}
            backLink="/ai"
          >
            <AIChatbotSettingsGeneral />
          </StickyRouteHeader>
        )
      },
      {
        path: "/ai/settings/training/:id",
        element: (
          <StickyRouteHeader
            title="AI Chatbot"
            headerActions={<SaveChatbotButton></SaveChatbotButton>}
            backLink="/ai"
          >
            <AIChatbotSettingsTraining />
          </StickyRouteHeader>
        )
      },
      {
        path: "/ai/settings/handoff/:id",
        element: (
          <StickyRouteHeader
            title="AI Chatbot"
            headerActions={<SaveChatbotButton></SaveChatbotButton>}
            backLink="/ai"
          >
            <AIChatbotSettingsHandoff />
          </StickyRouteHeader>
        )
      },
      {
        path: "/ai/settings/playground/:id",
        element: (
          <StickyRouteHeader
            title="AI Chatbot"
            headerActions={<SaveChatbotButton></SaveChatbotButton>}
            backLink="/ai"
          >
            <AIChatbotSettingsPlayground />
          </StickyRouteHeader>
        )
      },
      // {
      //   path: "/ai/settings/data/:id",
      //   element: (
      //     <StickyRouteHeader
      //       title="AI Chatbot"
      //       headerActions={<SaveChatbotButton></SaveChatbotButton>}
      //       backLink="/ai"
      //     >
      //       <AIChatbotSettingsData />
      //     </StickyRouteHeader>
      //   )
      // },
      // {
      //   path: "/ai/settings/questions/:id",
      //   element: (
      //     <StickyRouteHeader
      //       title="AI Chatbot"
      //       headerActions={<SaveChatbotButton></SaveChatbotButton>}
      //       backLink="/ai"
      //     >
      //       <AIChatbotSettingsStarterQuestions />
      //     </StickyRouteHeader>
      //   )
      // },
      // {
      //   path: "/ai/settings/test/:id",
      //   element: (
      //     <StickyRouteHeader
      //       title="AI Chatbot"
      //       headerActions={<SaveChatbotButton></SaveChatbotButton>}
      //       backLink="/ai"
      //     >
      //       <AIChatbotSettingsTest />
      //     </StickyRouteHeader>
      //   )
      // },
      {
        path: "/ai-new/settings",
        element: (
          <StickyRouteHeader
            title="AI Chatbot (New)"
            headerActions={<AINewCreateChatbotButton />}
          >
            <AINewSettings />
          </StickyRouteHeader>
        )
      },
      {
        path: "/ai-new/settings/general/:id",
        element: (
          <StickyRouteHeader
            title="AI Chatbot (New)"
            headerActions={<AINewSaveChatbotButton />}
            backLink="/ai-new/settings"
          >
            <AINewSettingsGeneral />
          </StickyRouteHeader>
        )
      },
      {
        path: "/ai-new/settings/handoff/:id",
        element: (
          <StickyRouteHeader
            title="AI Chatbot (New)"
            headerActions={<AINewSaveChatbotButton />}
            backLink="/ai-new/settings"
          >
            <AINewSettingsHandoff />
          </StickyRouteHeader>
        )
      },
      {
        path: "/ai-new/settings/playground/:id",
        element: (
          <StickyRouteHeader
            title="AI Chatbot (New)"
            headerActions={<AINewSaveChatbotButton />}
            backLink="/ai-new/settings"
          >
            <AINewSettingsPlayground />
          </StickyRouteHeader>
        )
      },
      {
        path: "/ai-new/training",
        element: (
          <StickyRouteHeader title="AI Chatbot (New)">
            <AINewTraining />
          </StickyRouteHeader>
        )
      },
      {
        path: "/ai-new/chat",
        element: (
          <StickyRouteHeader title="AI Chatbot (New)">
            <AINewChat />
          </StickyRouteHeader>
        )
      },
      {
        path: "/ai-new/indexes",
        element: (
          <StickyRouteHeader title="AI Chatbot (New)">
            <AINewIndexes />
          </StickyRouteHeader>
        )
      },
      {
        path: "/rules",
        element: (
          <RouteHeader title="Rules">
            <RuleManager />
          </RouteHeader>
        )
      },
      {
        path: "/files",
        element: (
          <RouteHeader title="File Upload">
            <FileStorage />
          </RouteHeader>
        )
      },
      {
        path: "/workflows/create",
        element: (
          <RouteHeader title="Create New Workflow">
            <hr className="pb-6" />
            <WorkflowsCreate />
          </RouteHeader>
        )
      },
      {
        path: "/workflows",
        element: (
          <StickyRouteHeader
            title="Workflows"
            headerActions={<NewWorkflowButton />}
          >
            <Workflows />
          </StickyRouteHeader>
        )
      },
      {
        path: "/workflows/variable-guide",
        element: (
          <RouteHeader title="Variable Guide">
            <VariableGuide />
          </RouteHeader>
        )
      },
      {
        path: "/workflows/:id",
        element: <WorkflowsEditor />
      },
      {
        path: "/invitations",
        element: (
          <RouteHeader title="Invitations">
            <Invitations />
          </RouteHeader>
        )
      },
      {
        path: "/skills",
        element: (
          <RouteHeader title="Skills">
            <Skills />
          </RouteHeader>
        )
      },

      {
        path: "/kb/articles",
        element: (
          <RouteHeader title="KB Articles">
            <ArticlesIndex />
          </RouteHeader>
        )
      },
      {
        path: "/kb/settings",
        element: (
          <RouteHeader title="KB Settings">
            <SettingsIndex />
          </RouteHeader>
        )
      },
      {
        path: "/kb/articles/create",
        element: (
          <RouteHeader title="Create KB Article">
            <ArticlesCreate />
          </RouteHeader>
        )
      },
      //todo this id needs fixed
      {
        path: "/kb/articles/edit/:id",
        element: (
          <RouteHeader title="Edit KB Article">
            <ArticlesEdit />
          </RouteHeader>
        )
      },
      {
        path: "/kb/topics",
        element: (
          <RouteHeader title="KB Topics">
            <TopicsIndex />
          </RouteHeader>
        )
      },
      {
        path: "/kb/map",
        element: (
          <RouteHeader title="KB Site Map">
            <SiteMap />
          </RouteHeader>
        )
      },
      {
        path: "/kb/setup",
        element: (
          <RouteHeader title="KB Setup">
            <SetupIndex />
          </RouteHeader>
        )
      },
      {
        path: "/messaging/setup",
        element: <MessagingSetup />
      }
    ]
  }
];

export function AppRoutes() {
  const auth = useAuth();

  const mainLoader = useCallback(
    async ({ request }: any) => {
      const url = new URL(request.url);
      const route = url.pathname;
      if (auth.isAuthenticated() && route === "/") {
        return redirect("/");
      }
      return null;
    },
    [auth]
  );

  const routes = createAppRoutes(mainLoader);
  const router = createBrowserRouter(routes);
  return <RouterProvider router={router} />;
}
